import { detailPageChipSx } from '@farmshare/ui-components';
import { Box, Chip, type ChipProps } from '@mui/material';

import { JobTagIcon } from './job-tag-icon';

interface JobTagChipProps extends Omit<ChipProps, 'icon' | 'label'> {
  tag: { _id?: string; name: string; icon: string };
}

/**
 * The tag chip's dimensions: the detail-page header's own meta-chip sizing
 * (`detailPageChipSx`), because that row is the tightest place a tag appears
 * and a tag there has to line up with the customer and date beside it. Taken
 * from the header rather than restated, so the two can't drift apart.
 *
 * Exported for a chip that sits among tags without being one — the `+3`
 * overflow — so it can match them.
 */
export const jobTagChipSx = {
  ...detailPageChipSx,
  '&.MuiChip-outlined': { borderColor: 'divider' },
} as const;

/**
 * A job tag as a chip — icon plus name.
 *
 * The one place tag-chip sizing and spacing are decided, so every surface
 * showing a full tag (job header, job sidebar, calendar tooltip, the pickers,
 * the settings preview) matches. Callers place it; they don't resize it.
 *
 * The icon goes INSIDE the label rather than in Chip's `icon` slot. That slot
 * is styled for an icon smaller than its text and pins it hard against the
 * chip's left edge with margins whose rules are a specificity fight to
 * override; a tag icon is about cap height, so it lands flush and looks
 * broken. In the label it just inherits the label's own symmetric padding.
 * @param root0
 * @param root0.tag
 */
export function JobTagChip({ tag, sx = undefined, ...props }: JobTagChipProps) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={
        // `flex`, not `inline-flex`: an inline box sits on the label's text
        // baseline, and its own baseline is the bottom of the icon, so the
        // whole label rides a couple of pixels high in the chip.
        <Box
          component="span"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
        >
          <JobTagIcon
            iconKey={tag.icon}
            sx={{
              fontSize: 13,
              flexShrink: 0,
              // Solid glyphs at the text's colour read heavier than the text;
              // the header's other chips grey their icons for the same
              // reason. A filled chip keeps its contrast colour.
              color: props.variant === 'filled' ? 'inherit' : 'text.secondary',
            }}
          />
          <Box
            component="span"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {tag.name}
          </Box>
        </Box>
      }
      {...props}
      sx={[jobTagChipSx, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
}
